import { useMemo, useState, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Check, Star, Minus, Plus, X, Sparkles } from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { StickyBuyBar } from "@/components/marketing/StickyBuyBar";
import { useCurrency } from "@/lib/ecom/currency";
import { useCart } from "@/lib/ecom/cart";
import { productBySlug, formatMoney } from "@/lib/ecom/catalog";
import {
  GR_SLUG, GR_BASE_PRICE, GR_HERO, GR_FEATURES_IMG,
  type GoogleReviewDict,
} from "@/lib/shop/products/google-review";

export function buildGoogleReviewHead(dict: GoogleReviewDict) {
  return {
    meta: [
      { title: dict.seo.title },
      { name: "description", content: dict.seo.description },
      { property: "og:title", content: dict.seo.ogTitle },
      { property: "og:description", content: dict.seo.ogDescription },
      { property: "og:type", content: "product" },
      { property: "og:image", content: GR_HERO },
      ...(dict.locale ? [{ property: "og:locale", content: dict.locale }] : []),
      { property: "twitter:card", content: "summary_large_image" },
      { property: "twitter:image", content: GR_HERO },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org/",
          "@type": "Product",
          name: dict.productName,
          image: [GR_HERO, GR_FEATURES_IMG],
          description: dict.jsonLdDescription,
          brand: { "@type": "Brand", name: "Banana Cards" },
          sku: "BC-GOOGLE-REVIEW",
          category: "Marketing & Reputation",
          offers: {
            "@type": "Offer",
            url: dict.productUrl,
            priceCurrency: "USD",
            price: GR_BASE_PRICE.toFixed(2),
            availability: "https://schema.org/InStock",
            itemCondition: "https://schema.org/NewCondition",
          },
          aggregateRating: { "@type": "AggregateRating", ratingValue: "4.84", reviewCount: "289" },
        }),
      },
    ],
  };
}

export function GoogleReviewPage({ dict }: { dict: GoogleReviewDict }) {
  const BUNDLES = dict.bundles;
  const GALLERY = dict.gallery;
  const FEATURES = dict.features.items;
  const FAQ = dict.faq.items;
  const COMPARE = dict.compare;
  const STEPS = dict.steps.items;
  const INDUSTRIES = dict.industries.items;

  const [bundleQty, setBundleQty] = useState<number>(BUNDLES[2]?.qty ?? BUNDLES[0].qty);
  const [qty, setQty] = useState<number>(1);
  const [active, setActive] = useState<number>(0);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const heroRef = useRef<HTMLElement>(null);

  const { country } = useCurrency();
  const { addItem } = useCart();
  const product = productBySlug(GR_SLUG)!;
  const basePrice = product.prices[country.code] ?? product.prices.US ?? GR_BASE_PRICE;
  const bundle = BUNDLES.find((b) => b.qty === bundleQty)!;
  const unitPrice  = useMemo(() => basePrice * bundle.perCard, [basePrice, bundle]);
  const totalPrice = useMemo(() => Math.round(unitPrice * bundle.qty * qty * 100) / 100, [unitPrice, bundle, qty]);
  const compareAt  = useMemo(() => basePrice * bundle.qty * qty, [basePrice, bundle, qty]);

  return (
    <SiteLayout>
      <section ref={heroRef} className="relative">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 pb-16 pt-12 md:grid-cols-12 md:px-6 md:pt-16">
          <div className="md:col-span-7">
            <button type="button" onClick={() => setLightbox(active)} className="block w-full overflow-hidden rounded-3xl bg-muted ring-1 ring-foreground/5" aria-label="Open image">
              <img src={GALLERY[active].src} alt={GALLERY[active].alt} loading="eager" fetchPriority="high" width={1600} height={1280} className="aspect-[5/4] w-full object-cover" />
            </button>
            <div className="mt-3 grid grid-cols-2 gap-3">
              {GALLERY.map((g, i) => (
                <button key={i} type="button" onClick={() => setActive(i)} className={`overflow-hidden rounded-2xl bg-muted ring-1 transition ${active === i ? "ring-foreground" : "ring-foreground/5 hover:ring-foreground/30"}`} aria-pressed={active === i}>
                  <img src={g.src} alt={g.alt} loading="lazy" width={400} height={300} className="aspect-[4/3] w-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <div className="md:col-span-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">{dict.eyebrow}</p>
            <h1 className="mt-3 font-display text-3xl leading-[1.1] md:text-[2.5rem]">{dict.productName}</h1>
            <div className="mt-3 flex items-center gap-2">
              <div className="flex">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-foreground text-foreground" />)}</div>
              <span className="text-xs font-semibold">{dict.ratingLabel}</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-[15px]">{dict.intro}</p>

            <div className="mt-6 flex items-end gap-3">
              <p className="font-display text-4xl leading-none">{formatMoney(totalPrice, country)}</p>
              {bundle.save && (
                <>
                  <p className="text-lg text-muted-foreground line-through">{formatMoney(compareAt, country)}</p>
                  <span className="rounded-full bg-brand-lime px-2.5 py-1 text-[11px] font-bold text-brand-lime-foreground">{dict.saveBadge(bundle.save)}</span>
                </>
              )}
            </div>
            <p className="mt-1.5 text-[12px] text-muted-foreground">{formatMoney(unitPrice, country)} {dict.perCardSuffix}</p>

            <div className="mt-7">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{dict.buyBox.bundleEyebrow}</p>
                <p className="text-xs font-semibold">{bundle.label}</p>
              </div>
              <div className="mt-3 grid gap-2">
                {BUNDLES.map((b) => {
                  const isActive = b.qty === bundleQty;
                  const perCard = basePrice * b.perCard;
                  return (
                    <button key={b.qty} type="button" onClick={() => setBundleQty(b.qty)} className={`flex items-center justify-between gap-4 rounded-xl p-3.5 text-left transition ${isActive ? "border-[3px] border-foreground bg-card" : "border border-foreground/10 bg-card hover:border-[3px] hover:border-foreground/30"}`} aria-pressed={isActive}>
                      <span className="flex items-center gap-3">
                        <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${isActive ? "border-foreground bg-foreground" : "border-foreground/25"}`}>
                          {isActive && <Check className="h-3 w-3 text-background" strokeWidth={3} />}
                        </span>
                        <span>
                          <span className="block text-sm font-bold">{b.label}</span>
                          <span className="block text-[12px] text-muted-foreground">{formatMoney(perCard, country)} / {dict.bundleSection.perCardLabel}</span>
                        </span>
                      </span>
                      <span className="text-right">
                        {b.badge && (
                          <span className={`inline-block rounded-full px-2 py-0.5 text-[10.5px] font-bold ${b.qty === 5 ? "bg-brand-lime text-brand-lime-foreground" : "bg-foreground/[0.06] text-foreground"}`}>{b.badge}</span>
                        )}
                        <span className="mt-1 block text-sm font-bold">{formatMoney(perCard * b.qty, country)}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-7 flex items-stretch gap-3">
              <div className="flex items-center gap-1 rounded-full border border-foreground/15 bg-card px-1">
                <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="rounded-full p-2 hover:bg-muted" aria-label="-"><Minus className="h-4 w-4" /></button>
                <span className="min-w-[2ch] text-center text-sm font-bold">{qty}</span>
                <button type="button" onClick={() => setQty((q) => Math.min(99, q + 1))} className="rounded-full p-2 hover:bg-muted" aria-label="+"><Plus className="h-4 w-4" /></button>
              </div>
              <button type="button" onClick={() => addItem(GR_SLUG, bundleQty, qty)} className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-foreground px-6 py-3 text-sm font-bold text-background transition hover:opacity-90">
                {dict.buyBox.addToCart} · {formatMoney(totalPrice, country)} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            

            <ul className="mt-7 grid gap-2.5 border-t border-foreground/10 pt-6 text-sm">
              {dict.usps.map((u) => {
                const Icon = u.icon;
                return (
                  <li key={u.label} className="flex items-start gap-2.5">
                    <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
                    <span className="font-medium">{u.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      {/* BUNDLES highlight */}
      <section className="border-y border-foreground/5 bg-muted/40">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6">
          <div className="mb-10 flex flex-col items-start gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{dict.bundleSection.eyebrow}</p>
              <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
                {dict.bundleSection.headingPre} <em className="italic">{dict.bundleSection.headingEm}</em>
              </h2>
            </div>
            <p className="max-w-sm text-sm text-muted-foreground">{dict.bundleSection.sub}</p>
          </div>
          <div className="grid gap-3 md:grid-cols-4">
            {BUNDLES.map((b) => {
              const perCard = basePrice * b.perCard;
              const total = Math.round(perCard * b.qty * 100) / 100;
                const popular = b.qty === 5;
                const selected = bundleQty === b.qty;
                const borderClass = selected
                  ? "border-[3px] border-foreground"
                  : popular
                  ? "border border-foreground"
                  : "border border-foreground/5 hover:border-[3px] hover:border-foreground";
                return (
                  <button key={b.qty} type="button" onClick={() => setBundleQty(b.qty)} className={`relative rounded-2xl bg-card p-6 text-left transition hover:-translate-y-0.5 ${borderClass}`}>
                  {b.badge && (
                    <span className={`absolute -top-3 left-6 inline-flex h-6 items-center justify-center rounded-full px-2.5 text-[10.5px] font-bold ${popular ? "bg-brand-lime text-brand-lime-foreground" : "bg-foreground text-background"}`}>{b.badge}</span>
                  )}
                  <p className="font-display text-3xl leading-none">{b.qty}×</p>
                  <p className="mt-1 text-sm font-semibold">{b.label}</p>
                  <div className="mt-5 flex items-baseline gap-1.5">
                    <p className="font-display text-2xl leading-none">{formatMoney(total, country)}</p>
                    {b.save && <p className="text-xs text-muted-foreground line-through">{formatMoney(basePrice * b.qty, country)}</p>}
                  </div>
                  <p className="mt-1.5 text-[12px] text-muted-foreground">{formatMoney(perCard, country)} {dict.bundleSection.perCardLabel}</p>
                  <span className="mt-5 inline-flex items-center gap-1 text-xs font-bold underline-offset-4 hover:underline">
                    {bundleQty === b.qty ? dict.bundleSection.selected : dict.bundleSection.choose} <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURE GRID */}
      <section className="mx-auto max-w-7xl px-4 py-24 md:px-6">
        <div className="mb-12 max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{dict.features.eyebrow}</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
            {dict.features.headingPre} <em className="italic">{dict.features.headingEm}</em>
          </h2>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="rounded-2xl bg-card p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-50">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <h3 className="mt-5 text-lg font-bold">{f.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CARD ANATOMY */}
      <section className="bg-muted/40">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-24 md:grid-cols-2 md:px-6">
          <div className="relative">
            <img src={GR_FEATURES_IMG} alt={dict.anatomy.imageAlt} loading="lazy" width={1600} height={1280} className="w-full rounded-3xl object-cover" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{dict.anatomy.eyebrow}</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              {dict.anatomy.headingPre} <em className="italic">{dict.anatomy.headingEm}</em>
            </h2>
            <ul className="mt-7 grid gap-4 text-sm">
              {dict.anatomy.items.map((b) => (
                <li key={b.t} className="flex items-start gap-3">
                  <Check className="mt-1 h-4 w-4 shrink-0" />
                  <span>
                    <span className="block font-bold">{b.t}</span>
                    <span className="block text-muted-foreground">{b.d}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="mx-auto max-w-7xl px-4 py-24 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{dict.industries.eyebrow}</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            {dict.industries.headingPre} <em className="italic">{dict.industries.headingEm}</em>
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-base">{dict.industries.sub}</p>
        </div>
        <div className="mt-12 grid gap-3 sm:grid-cols-2 md:grid-cols-3">
          {INDUSTRIES.map((i) => {
            const Icon = i.icon;
            return (
              <div key={i.label} className="flex items-center gap-4 rounded-2xl bg-card p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-50">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <p className="text-sm font-bold">{i.label}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* SOCIAL PROOF stat band */}
      <section className="bg-brand-dark text-background">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="grid gap-10 md:grid-cols-3">
            {dict.stats.map((s) => (
              <div key={s.label}>
                <p className="font-display text-6xl leading-none text-brand-lime md:text-7xl">{s.stat}</p>
                <p className="mt-3 max-w-xs text-sm text-background/75">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="mx-auto max-w-7xl px-4 py-24 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{COMPARE.eyebrow}</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            {COMPARE.headingPre} <em className="italic">{COMPARE.headingEm}</em>
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-base">{COMPARE.sub}</p>
        </div>
        <div className="mt-12 grid gap-4 md:grid-cols-2">
          <div className="rounded-3xl bg-card p-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{COMPARE.paperLabel}</p>
            <ul className="mt-6 grid gap-3 text-sm">
              {COMPARE.paper.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-muted-foreground">
                  <X className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative overflow-hidden rounded-3xl bg-brand-dark p-8 text-background">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_10%,oklch(0.96_0.21_117/0.18),transparent_55%)]" aria-hidden />
            <p className="relative text-xs font-bold uppercase tracking-[0.2em] text-brand-lime">{dict.productName}</p>
            <ul className="relative mt-6 grid gap-3 text-sm">
              {COMPARE.product.map((p) => (
                <li key={p} className="flex items-start gap-2.5">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-lime" />
                  <span className="font-medium text-background/95">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-muted/40">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{dict.steps.eyebrow}</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              {dict.steps.headingPre} <em className="italic">{dict.steps.headingEm}</em>
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-base">{dict.steps.sub}</p>
          </div>
          <ol className="mt-12 grid gap-4 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <li key={s.title} className="relative rounded-2xl bg-card p-6">
                <span className="absolute -top-3 left-6 inline-flex h-7 items-center justify-center rounded-full bg-foreground px-3 text-[11px] font-bold text-background">{dict.steps.stepLabel(i + 1)}</span>
                <h3 className="mt-3 text-base font-bold">{s.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-4 py-24 md:px-6">
        <div className="mb-10 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{dict.faq.eyebrow}</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            {dict.faq.headingPre} <em className="italic">{dict.faq.headingEm}</em>
          </h2>
        </div>
        <div className="divide-y divide-foreground/10 rounded-2xl bg-card">
          {FAQ.map((f) => (
            <details key={f.q} className="group p-5">
              <summary className="flex cursor-pointer items-center justify-between gap-4 text-sm font-bold">
                {f.q}
                <ArrowRight className="h-4 w-4 shrink-0 transition group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
            }),
          }}
        />
      </section>

      {/* OUTRO */}
      <section className="mx-auto max-w-7xl px-4 pb-24 md:px-6">
        <div className="relative overflow-hidden rounded-3xl bg-foreground px-8 py-16 text-center text-background md:p-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(0.96_0.21_117/0.14),transparent_55%)]" aria-hidden />
          <Sparkles className="mx-auto h-6 w-6 text-brand-lime" />
          <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
            {dict.outro.headingPre} <em className="italic text-brand-lime">{dict.outro.headingEm}</em>
          </h2>
          <p className="mx-auto mt-5 max-w-lg text-base text-background/70">{dict.outro.body}</p>
          <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
            <a href="#top" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground">{dict.outro.primaryCta}</a>
            <Link to="/shop" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold">{dict.outro.secondaryCta}</Link>
          </div>
        </div>
      </section>

      {/* LIGHTBOX */}
      {lightbox !== null && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4" onClick={() => setLightbox(null)} role="dialog" aria-modal="true">
          <button type="button" onClick={() => setLightbox(null)} className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
          <img src={GALLERY[lightbox].src} alt={GALLERY[lightbox].alt} className="max-h-[90vh] max-w-[92vw] rounded-2xl object-contain" />
        </div>
      )}
      <StickyBuyBar
        triggerRef={heroRef}
        name={dict.productName}
        image={GR_HERO}
        price={formatMoney(totalPrice, country)}
        subtitle={`${bundle.label}${bundle.save ? ` · ${dict.saveBadge(bundle.save)}` : ""}`}
        onAdd={() => addItem(GR_SLUG, bundleQty, qty)}
      />
    </SiteLayout>
  );
}